/** The compose item's current from-address, or undefined when the host
 *  can't tell us. Brand resolution keys on it (server + last-good cache). */
export function getCurrentFromAddress(item) {
    return new Promise((resolve) => {
        try {
            item.from.getAsync((result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded &&
                    result.value?.emailAddress) {
                    resolve(result.value.emailAddress);
                }
                else
                    resolve(undefined);
            });
        }
        catch {
            resolve(undefined);
        }
    });
}
function attachInline(item, img) {
    return new Promise((resolve, reject) => {
        item.addFileAttachmentFromBase64Async(img.base64, img.cid, { isInline: true }, (result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded)
                resolve();
            else
                reject(new Error(result.error?.message ??
                    `addFileAttachmentFromBase64Async failed (${img.cid})`));
        });
    });
}
function setSignature(item, html) {
    return new Promise((resolve, reject) => {
        item.body.setSignatureAsync(html, { coercionType: Office.CoercionType.Html }, (result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded)
                resolve();
            else
                reject(new Error(result.error?.message ?? "setSignatureAsync failed"));
        });
    });
}
/**
 * Attach all inline images (brand logo + optional MHFA badge + any
 * custom-block extra attachments) then set signature HTML.
 *
 * Must be called inside an event handler — `item` comes from
 * Office.context.mailbox.item. cid filename in HTML must match attachment
 * filename exactly (memory #infra: cid filename gotcha).
 */
export async function applySignature(item, payload) {
    // Inline-attach legacy cid images only when the api still ships them
    // (kept for backwards compat with any cached add-in build). Current api
    // serves all images via HTTPS URL so these branches are skipped.
    if (payload.logo)
        await attachInline(item, payload.logo);
    if (payload.mhfaLogo)
        await attachInline(item, payload.mhfaLogo);
    if (payload.attachments?.length) {
        for (const a of payload.attachments) {
            await attachInline(item, a);
        }
    }
    // Stamp a per-call marker so OWA/New Outlook detects content change.
    // setSignatureAsync no-ops on repeat in OWA when the HTML hash matches the
    // previously-applied signature — without the marker the brand-switch click
    // reports Succeeded but the body never updates.
    const stamped = `<!--sig:${Date.now()}-->${payload.html}`;
    await setSignature(item, stamped);
}
